import { Navigate, Route, Routes } from "react-router-dom";

import { OrganisationPermissionConstants, ResourcePermissionConstants } from "../constants";
import {
  ActivityPageParams,
  PrivateActivityDocumentsParams,
  PrivateProjectDocumentsParams,
  PublicDevelopmentManagerDetailsPreviewParams,
  PublicProjectOverviewPreviewParams,
} from "../models";
import { AssetManagerDealPage } from "../route/asset-manager/deal";
import { ActivityEditPage, ActivityViewPage, ActivityWizardProvider } from "../route/developer/activities";
import { DocumentTypesTab, DocumentTypeViewPage } from "../route/developer/settings/document-types";
import { DevelopmentManagerActivitiesPage } from "../route/development-manager/activities";
import { DevelopmentManagerDashboardPage } from "../route/development-manager/dashboard";
import {
  DevelopmentManagerChangePasswordPage,
  DevelopmentManagerChangePersonalDetailsPage,
} from "../route/development-manager/profile";
import { ProjectPage } from "../route/development-manager/projects";
import {
  DevelopmentManagerMarketingAssetsTab,
  DevelopmentManagerSettingsPage,
} from "../route/development-manager/settings";
import { DevelopmentManagerOrganisationTab } from "../route/development-manager/settings/organisation";
import {
  DevelopmentManagerUsersEditPage,
  DevelopmentManagerUsersListPage,
  DevelopmentManagerUsersSendInvitationPage,
} from "../route/development-manager/settings/users";
import { PublicPage } from "../route/public";
import { PublicDevelopmentManagerDetailsPreviewPage } from "../route/public/development-manager";
import { PublicProjectOverviewPreviewPage, PublicProjectPreviewContainer } from "../route/public/project";
import {
  ActivityDashboardProvider,
  ActivityDiscussionProvider,
  ActivityDiscussionTab,
  ActivityPage,
  ActivityTasksTab,
  DocumentsTab as ActivityDocumentsTab,
  VersionHistoryTab,
} from "../route/shared/activities-dashboard";
import { ActivityDocumentHistoryPage } from "../route/shared/activities-dashboard/documents";
import { AssetManagerCompaniesListPage as CompaniesListPage } from "../route/shared/companies";
import { Error403Page, Error404Page, Error500Page } from "../route/shared/errors";
import { SharedInventoryManagementBoard } from "../route/shared/inventory";
import {
  LandholdingDetailsTab,
  LandholdingDocumentsTab,
  LandholdingInventoryTab,
  LandholdingLocationTab,
  LandholdingPage,
  LandholdingProvider,
  LandholdingTasksTab,
} from "../route/shared/landholding";
import {
  DocumentProvider,
  DocumentsTabWithPreAccess,
  GroupProvider,
  LocationTab,
  NewProjectJourneyPage,
  OverviewTabWithPreAccess,
  ProjectAccessAcceptInvitationPage,
  ProjectAccessDetailsPage,
  ProjectAccessInvitePage,
  ProjectAccessTab,
  ProjectActivitiesTab,
  ProjectDetailsTab,
  ProjectDocumentHistoryPage,
  ProjectOverviewManageProjectGroupPage,
  ProjectProvider,
  SharedProjectsPage,
} from "../route/shared/projects";
import { InventoryTabWithPreAccess, MarketingSettingsTab, TasksTab } from "../route/shared/projects/project";
import { ProjectRoadmapPage } from "../route/shared/projects/project-roadmap";
import { ProjectTrackerPage } from "../route/shared/projects/project-tracker";
import { AuditHistoryTab } from "../route/shared/settings/audit-history";
import { SettingsProjectAccessInvitePage, SettingsProjectAccessTab } from "../route/shared/settings/project-access";
import {
  SharedWorkflowTemplatePage,
  SharedWorkflowTemplatesPage,
  TasksBoardPage,
  TasksBoardProvider,
  TasksDashboardPage,
  TasksDashboardProvider,
  WorkflowTemplateProvider,
} from "../route/shared/workflows";
import { DeveloperSingleCardTemplate } from "../templates";
import { TagsProvider } from "../templates/TagsPanel/TagsContext";
import { useRouteGuards } from "./guards";

export const DevelopmentManagerRoutes = (): JSX.Element => {
  const { RequireDevelopmentManagerAuth, RequireUserPermission } = useRouteGuards();

  return (
    <div className="development-manager-wrapper">
      <Routes>
        <Route
          path="dashboard"
          element={
            <RequireDevelopmentManagerAuth>
              <DevelopmentManagerDashboardPage />
            </RequireDevelopmentManagerAuth>
          }
        />
        <Route
          path="operations/companies"
          element={
            <RequireDevelopmentManagerAuth>
              <DeveloperSingleCardTemplate activeElement="CRM" pageTitle="CRM">
                <CompaniesListPage />
              </DeveloperSingleCardTemplate>
            </RequireDevelopmentManagerAuth>
          }
        />
        <Route
          path="inventory"
          element={
            <RequireDevelopmentManagerAuth>
              <TagsProvider>
                <SharedInventoryManagementBoard />
              </TagsProvider>
            </RequireDevelopmentManagerAuth>
          }
        />
        <Route
          path="activities"
          element={
            <RequireDevelopmentManagerAuth>
              <DevelopmentManagerActivitiesPage />
            </RequireDevelopmentManagerAuth>
          }
        />
        <Route
          path={`activities/:${ActivityPageParams.activityHistoryUuid}/edit`}
          element={
            <RequireDevelopmentManagerAuth>
              <ActivityWizardProvider>
                <ActivityEditPage />
              </ActivityWizardProvider>
            </RequireDevelopmentManagerAuth>
          }
        />
        <Route
          path={`activities/:${ActivityPageParams.activityHistoryUuid}/view`}
          element={
            <RequireDevelopmentManagerAuth>
              <ActivityWizardProvider>
                <ActivityViewPage />
              </ActivityWizardProvider>
            </RequireDevelopmentManagerAuth>
          }
        />
        <Route
          path={`activities/:${ActivityPageParams.activityHistoryUuid}/view?:location`}
          element={
            <RequireDevelopmentManagerAuth>
              <ActivityViewPage />
            </RequireDevelopmentManagerAuth>
          }
        />
        <Route
          path={`activity/:${ActivityPageParams.activityUuid}/document/:${PrivateActivityDocumentsParams.activityDocumentUuid}`}
          element={
            <RequireDevelopmentManagerAuth>
              <ActivityDashboardProvider>
                <ActivityDocumentHistoryPage />
              </ActivityDashboardProvider>
            </RequireDevelopmentManagerAuth>
          }
        />

        <Route
          path={`activity/:${ActivityPageParams.activityUuid}/*`}
          element={
            <ActivityDashboardProvider>
              <ActivityPage />
            </ActivityDashboardProvider>
          }
        >
          <Route
            path="discussions"
            element={
              <ActivityWizardProvider>
                <RequireDevelopmentManagerAuth>
                  <ActivityDiscussionProvider>
                    <ActivityDiscussionTab />
                  </ActivityDiscussionProvider>
                </RequireDevelopmentManagerAuth>
              </ActivityWizardProvider>
            }
          />
          <Route
            path="documents"
            element={
              <RequireDevelopmentManagerAuth>
                <ActivityDocumentsTab />
              </RequireDevelopmentManagerAuth>
            }
          />
          <Route
            path="history"
            element={
              <ActivityWizardProvider>
                <RequireDevelopmentManagerAuth>
                  <VersionHistoryTab />
                </RequireDevelopmentManagerAuth>
              </ActivityWizardProvider>
            }
          />
          <Route
            path="tasks"
            element={
              <RequireDevelopmentManagerAuth>
                <ActivityTasksTab />
              </RequireDevelopmentManagerAuth>
            }
          />
        </Route>
        <Route
          path="projects/new-project"
          element={
            <RequireDevelopmentManagerAuth>
              <RequireUserPermission userPermission={ResourcePermissionConstants.CREATE_RESOURCE}>
                <NewProjectJourneyPage />
              </RequireUserPermission>
            </RequireDevelopmentManagerAuth>
          }
        />
        <Route
          path="projects/*"
          element={
            <RequireDevelopmentManagerAuth>
              <SharedProjectsPage />
            </RequireDevelopmentManagerAuth>
          }
        >
          <Route
            path="project-tracker"
            element={
              <RequireDevelopmentManagerAuth>
                <ProjectTrackerPage />
              </RequireDevelopmentManagerAuth>
            }
          />
          <Route
            path="project-roadmap"
            element={
              <RequireDevelopmentManagerAuth>
                <ProjectRoadmapPage />
              </RequireDevelopmentManagerAuth>
            }
          />
          <Route path="" element={<Navigate to="project-tracker" replace />} />
        </Route>

        <Route
          path="workflows/templates"
          element={
            <RequireDevelopmentManagerAuth>
              <SharedWorkflowTemplatesPage />
            </RequireDevelopmentManagerAuth>
          }
        />
        <Route
          path="workflows/tasks"
          element={
            <TasksDashboardProvider>
              <RequireDevelopmentManagerAuth>
                <TasksDashboardPage />
              </RequireDevelopmentManagerAuth>
            </TasksDashboardProvider>
          }
        />
        <Route
          path="workflows/templates/:workflowTemplateUuid"
          element={
            <WorkflowTemplateProvider>
              <RequireDevelopmentManagerAuth>
                <SharedWorkflowTemplatePage />
              </RequireDevelopmentManagerAuth>
            </WorkflowTemplateProvider>
          }
        />

        <Route
          path="deal/:dealUuid/listing"
          element={
            <RequireDevelopmentManagerAuth>
              <TagsProvider>
                <AssetManagerDealPage />
              </TagsProvider>
            </RequireDevelopmentManagerAuth>
          }
        />
        <Route
          path="workflows/tasks-board/:workflowUuid"
          element={
            <TasksBoardProvider>
              <RequireDevelopmentManagerAuth>
                <TasksBoardPage />
              </RequireDevelopmentManagerAuth>
            </TasksBoardProvider>
          }
        />
        <Route
          path="projects/:projectUuid/*"
          element={
            <ProjectProvider>
              <TagsProvider>
                <ProjectPage />
              </TagsProvider>
            </ProjectProvider>
          }
        >
          <Route
            path="overview"
            element={
              <RequireDevelopmentManagerAuth>
                <OverviewTabWithPreAccess />
              </RequireDevelopmentManagerAuth>
            }
          />
          <Route
            path="details"
            element={
              <RequireDevelopmentManagerAuth>
                <ProjectDetailsTab />
              </RequireDevelopmentManagerAuth>
            }
          />
          <Route
            path="inventory"
            element={
              <RequireDevelopmentManagerAuth>
                <InventoryTabWithPreAccess />
              </RequireDevelopmentManagerAuth>
            }
          />
          <Route
            path="activities"
            element={
              <RequireDevelopmentManagerAuth>
                <ProjectActivitiesTab />
              </RequireDevelopmentManagerAuth>
            }
          />
          <Route
            path="documents"
            element={
              <RequireDevelopmentManagerAuth>
                <DocumentsTabWithPreAccess />
              </RequireDevelopmentManagerAuth>
            }
          />
          <Route
            path="location"
            element={
              <RequireDevelopmentManagerAuth>
                <LocationTab />
              </RequireDevelopmentManagerAuth>
            }
          />
          <Route
            path="marketing-settings"
            element={
              <RequireDevelopmentManagerAuth>
                <MarketingSettingsTab />
              </RequireDevelopmentManagerAuth>
            }
          />
          <Route
            path="tasks"
            element={
              <RequireDevelopmentManagerAuth>
                <TasksTab />
              </RequireDevelopmentManagerAuth>
            }
          />
          <Route
            path="access"
            element={
              <RequireDevelopmentManagerAuth>
                <ProjectAccessTab />
              </RequireDevelopmentManagerAuth>
            }
          />
        </Route>
        <Route
          path="projects/:projectUuid/access/invite"
          element={
            <RequireDevelopmentManagerAuth>
              <ProjectProvider>
                <ProjectAccessInvitePage />
              </ProjectProvider>
            </RequireDevelopmentManagerAuth>
          }
        />
        <Route
          path="projects/:projectUuid/access/invite/:projectOrganisationInvitationUuid"
          element={
            <RequireDevelopmentManagerAuth>
              <ProjectProvider>
                <ProjectAccessDetailsPage />
              </ProjectProvider>
            </RequireDevelopmentManagerAuth>
          }
        />
        <Route
          path="project/access/invite/accept/:projectOrganisationInvitationUuid"
          element={
            <RequireDevelopmentManagerAuth>
              <ProjectProvider>
                <ProjectAccessAcceptInvitationPage />
              </ProjectProvider>
            </RequireDevelopmentManagerAuth>
          }
        />
        <Route
          path={`/projects/:${PrivateProjectDocumentsParams.projectUuid}/documents/:${PrivateProjectDocumentsParams.projectDocumentUuid}`}
        >
          <Route
            path=""
            element={
              <RequireDevelopmentManagerAuth>
                <ProjectProvider>
                  <DocumentProvider>
                    <ProjectDocumentHistoryPage />
                  </DocumentProvider>
                </ProjectProvider>
              </RequireDevelopmentManagerAuth>
            }
          />
        </Route>
        <Route
          path="landholding/:landholdingUuid/*"
          element={
            <LandholdingProvider>
              <TagsProvider>
                <LandholdingPage />
              </TagsProvider>
            </LandholdingProvider>
          }
        >
          <Route
            path="details"
            element={
              <RequireDevelopmentManagerAuth>
                <LandholdingDetailsTab />
              </RequireDevelopmentManagerAuth>
            }
          />
          <Route
            path="location"
            element={
              <RequireDevelopmentManagerAuth>
                <LandholdingLocationTab />
              </RequireDevelopmentManagerAuth>
            }
          />
          <Route
            path="inventory"
            element={
              <RequireDevelopmentManagerAuth>
                <LandholdingInventoryTab />
              </RequireDevelopmentManagerAuth>
            }
          />
          <Route
            path="documents"
            element={
              <RequireDevelopmentManagerAuth>
                <LandholdingDocumentsTab />
              </RequireDevelopmentManagerAuth>
            }
          />
          <Route
            path="tasks"
            element={
              <RequireDevelopmentManagerAuth>
                <LandholdingTasksTab />
              </RequireDevelopmentManagerAuth>
            }
          />
        </Route>
        <Route
          path="landholding/:groupUuid/manage-landholding"
          element={
            <RequireDevelopmentManagerAuth>
              <GroupProvider>
                <ProjectOverviewManageProjectGroupPage />
              </GroupProvider>
            </RequireDevelopmentManagerAuth>
          }
        />
        <Route
          path="settings/*"
          element={
            <RequireDevelopmentManagerAuth>
              <DevelopmentManagerSettingsPage />
            </RequireDevelopmentManagerAuth>
          }
        >
          <Route
            path="users"
            element={
              <RequireUserPermission
                userPermission={OrganisationPermissionConstants.READ_ORGANISATION_USERS}
                redirectRoute="/dm/settings/organisation"
              >
                <DevelopmentManagerUsersListPage />
              </RequireUserPermission>
            }
          />

          <Route path="organisation" element={<DevelopmentManagerOrganisationTab />} />
          <Route path="marketing-assets" element={<DevelopmentManagerMarketingAssetsTab />} />
          <Route path="marketing-assets/:previewUuid" element={<DevelopmentManagerMarketingAssetsTab />} />
          <Route path="audit-history" element={<AuditHistoryTab />} />
          <Route path="project-access" element={<SettingsProjectAccessTab />} />
          <Route path="document-types" element={<DocumentTypesTab />} />
          <Route path="" element={<Navigate to="users" replace />} />
        </Route>
        <Route
          path={`development-manager/details/preview/:${PublicDevelopmentManagerDetailsPreviewParams.previewUuid}`}
          element={
            <RequireDevelopmentManagerAuth>
              <PublicDevelopmentManagerDetailsPreviewPage />
            </RequireDevelopmentManagerAuth>
          }
        />
        <Route
          path="settings/project-access/invite"
          element={
            <RequireDevelopmentManagerAuth>
              <SettingsProjectAccessInvitePage />
            </RequireDevelopmentManagerAuth>
          }
        />
        <Route
          path="settings/document-types/:typeUuid/view"
          element={
            <RequireDevelopmentManagerAuth>
              <DocumentTypeViewPage />
            </RequireDevelopmentManagerAuth>
          }
        />
        <Route
          path="settings/users/send-invitation"
          element={
            <RequireDevelopmentManagerAuth>
              <DevelopmentManagerUsersSendInvitationPage />
            </RequireDevelopmentManagerAuth>
          }
        />
        <Route
          path="settings/users/:userUuid/edit"
          element={
            <RequireDevelopmentManagerAuth>
              <DevelopmentManagerUsersEditPage />
            </RequireDevelopmentManagerAuth>
          }
        />
        <Route
          path="profile/change-password"
          element={
            <RequireDevelopmentManagerAuth>
              <DevelopmentManagerChangePasswordPage />
            </RequireDevelopmentManagerAuth>
          }
        />
        <Route
          path="profile/change-personal-details"
          element={
            <RequireDevelopmentManagerAuth>
              <DevelopmentManagerChangePersonalDetailsPage />
            </RequireDevelopmentManagerAuth>
          }
        />
        <Route path="project/*" element={<PublicPage />}>
          <Route
            path={`:${PublicProjectOverviewPreviewParams.uuid}/preview/:${PublicProjectOverviewPreviewParams.previewUuid}`}
            element={
              <RequireDevelopmentManagerAuth>
                <PublicProjectPreviewContainer />
              </RequireDevelopmentManagerAuth>
            }
          >
            <Route path="overview" element={<PublicProjectOverviewPreviewPage />} />
          </Route>
        </Route>
        <Route
          path="403/access-denied"
          element={
            <RequireDevelopmentManagerAuth>
              <Error403Page />
            </RequireDevelopmentManagerAuth>
          }
        />
        <Route
          path="500/application-error"
          element={
            <RequireDevelopmentManagerAuth>
              <Error500Page />
            </RequireDevelopmentManagerAuth>
          }
        />
        <Route
          path="*"
          element={
            <RequireDevelopmentManagerAuth>
              <Error404Page />
            </RequireDevelopmentManagerAuth>
          }
        />
      </Routes>
    </div>
  );
};
